import { z } from "zod";

import type { FilterKeys } from "../shared";
import { idSchema, transformerMdContentParam } from "../shared";
import { awardSchema, minimalAwardSchema, deminimalizeAward } from "./award";
import {
  certificationSchema,
  minimalCertificationSchema,
  deminimalizeCertification,
} from "./certification";
import { customSectionSchema } from "./custom-section";
import { educationSchema, minimalEducationSchema, deminimalizeEducation } from "./education";
import { experienceSchema, minimalExperienceSchema, deminimalizeExperience } from "./experience";
import { interestSchema, minimalInterestSchema, deminimalizeInterest } from "./interest";
import { languageSchema, minimalLanguageSchema, deminimalizeLanguage } from "./language";
import { profileSchema, minimalProfileSchema, deminimalizeProfile } from "./profile";
import { projectSchema, minimalProjectSchema, deminimalizeProject } from "./project";
import {
  publicationSchema,
  minimalPublicationSchema,
  deminimalizePublication,
} from "./publication";
import { referenceSchema, minimalReferenceSchema, deminimalizeReference } from "./reference";
import { skillSchema, minimalSkillSchema, deminimalizeSkill } from "./skill";
import { volunteerSchema, minimalVolunteerSchema, deminimalizeVolunteer } from "./volunteer";

// Schema
export const sectionSchema = z.object({
  name: z.string(),
  columns: z.number().min(1).max(5).default(1),
  separateLinks: z.boolean().default(true),
  visible: z.boolean().default(true),
});

// Schema
export const customSchema = sectionSchema.extend({
  id: idSchema,
  items: z.array(customSectionSchema),
});

export const sectionsSchema = z.object({
  summary: sectionSchema.extend({
    id: z.literal("summary"),
    content: transformerMdContentParam,
  }),
  awards: sectionSchema.extend({
    id: z.literal("awards"),
    items: z.array(awardSchema),
  }),
  certifications: sectionSchema.extend({
    id: z.literal("certifications"),
    items: z.array(certificationSchema),
  }),
  education: sectionSchema.extend({
    id: z.literal("education"),
    items: z.array(educationSchema),
  }),
  experience: sectionSchema.extend({
    id: z.literal("experience"),
    items: z.array(experienceSchema),
  }),
  volunteer: sectionSchema.extend({
    id: z.literal("volunteer"),
    items: z.array(volunteerSchema),
  }),
  interests: sectionSchema.extend({
    id: z.literal("interests"),
    items: z.array(interestSchema),
  }),
  languages: sectionSchema.extend({
    id: z.literal("languages"),
    items: z.array(languageSchema),
  }),
  profiles: sectionSchema.extend({
    id: z.literal("profiles"),
    items: z.array(profileSchema),
  }),
  projects: sectionSchema.extend({
    id: z.literal("projects"),
    items: z.array(projectSchema),
  }),
  publications: sectionSchema.extend({
    id: z.literal("publications"),
    items: z.array(publicationSchema),
  }),
  references: sectionSchema.extend({
    id: z.literal("references"),
    items: z.array(referenceSchema),
  }),
  skills: sectionSchema.extend({
    id: z.literal("skills"),
    items: z.array(skillSchema),
  }),
  custom: z.record(z.string(), customSchema),
});

export const minimalSectionsSchema = z.object({
  summary: z.object({
    content: transformerMdContentParam,
  }),
  awards: z.object({
    items: z.array(minimalAwardSchema),
  }),
  certifications: z.object({
    items: z.array(minimalCertificationSchema),
  }),
  education: z.object({
    items: z.array(minimalEducationSchema),
  }),
  experience: z.object({
    items: z.array(minimalExperienceSchema),
  }),
  volunteer: z.object({
    items: z.array(minimalVolunteerSchema),
  }),
  interests: z.object({
    items: z.array(minimalInterestSchema),
  }),
  languages: z.object({
    items: z.array(minimalLanguageSchema),
  }),
  profiles: z.object({
    items: z.array(minimalProfileSchema),
  }),
  projects: z.object({
    items: z.array(minimalProjectSchema),
  }),
  publications: z.object({
    items: z.array(minimalPublicationSchema),
  }),
  references: z.object({
    items: z.array(minimalReferenceSchema),
  }),
  skills: z.object({
    items: z.array(minimalSkillSchema),
  }),
});

// Detailed Types
export type Section = z.infer<typeof sectionSchema>;
export type Sections = z.infer<typeof sectionsSchema>;
export type MinimalSections = z.infer<typeof minimalSectionsSchema>;

export type SectionKey = "basics" | keyof Sections | `custom.${string}`;
export type SectionWithItem<T = unknown> = Sections[FilterKeys<Sections, { items: T[] }>];
export type SectionItem = SectionWithItem["items"][number];
export type CustomSectionGroup = z.infer<typeof customSchema>;

// Defaults
export const defaultSection: Section = {
  name: "",
  columns: 1,
  separateLinks: true,
  visible: true,
};

export const defaultSections: Sections = {
  summary: { ...defaultSection, id: "summary", name: "Summary", content: "" },
  awards: { ...defaultSection, id: "awards", name: "Awards", items: [] },
  certifications: { ...defaultSection, id: "certifications", name: "Certifications", items: [] },
  education: { ...defaultSection, id: "education", name: "Education", items: [] },
  experience: { ...defaultSection, id: "experience", name: "Experience", items: [] },
  volunteer: { ...defaultSection, id: "volunteer", name: "Volunteering", items: [] },
  interests: { ...defaultSection, id: "interests", name: "Interests", items: [] },
  languages: { ...defaultSection, id: "languages", name: "Languages", items: [] },
  profiles: { ...defaultSection, id: "profiles", name: "Profiles", items: [] },
  projects: { ...defaultSection, id: "projects", name: "Projects", items: [] },
  publications: { ...defaultSection, id: "publications", name: "Publications", items: [] },
  references: { ...defaultSection, id: "references", name: "References", items: [] },
  skills: { ...defaultSection, id: "skills", name: "Skills", items: [] },
  custom: {},
};

export const deminimalizeSections = (sections: MinimalSections): Sections => {
  return {
    ...defaultSections,
    summary: {
      ...defaultSections.summary,
      ...sections.summary,
    },
    awards: {
      ...defaultSections.awards,
      ...sections.awards,
      items: sections.awards.items.map(deminimalizeAward),
    },
    certifications: {
      ...defaultSections.certifications,
      ...sections.certifications,
      items: sections.certifications.items.map(deminimalizeCertification),
    },
    education: {
      ...defaultSections.education,
      ...sections.education,
      items: sections.education.items.map(deminimalizeEducation),
    },
    experience: {
      ...defaultSections.experience,
      ...sections.experience,
      items: sections.experience.items.map(deminimalizeExperience),
    },
    volunteer: {
      ...defaultSections.volunteer,
      ...sections.volunteer,
      items: sections.volunteer.items.map(deminimalizeVolunteer),
    },
    interests: {
      ...defaultSections.interests,
      ...sections.interests,
      items: sections.interests.items.map(deminimalizeInterest),
    },
    languages: {
      ...defaultSections.languages,
      ...sections.languages,
      items: sections.languages.items.map(deminimalizeLanguage),
    },
    profiles: {
      ...defaultSections.profiles,
      ...sections.profiles,
      items: sections.profiles.items.map(deminimalizeProfile),
    },
    projects: {
      ...defaultSections.projects,
      ...sections.projects,
      items: sections.projects.items.map(deminimalizeProject),
    },
    publications: {
      ...defaultSections.publications,
      ...sections.publications,
      items: sections.publications.items.map(deminimalizePublication),
    },
    references: {
      ...defaultSections.references,
      ...sections.references,
      items: sections.references.items.map(deminimalizeReference),
    },
    skills: {
      ...defaultSections.skills,
      ...sections.skills,
      items: sections.skills.items.map(deminimalizeSkill),
    },
  };
};

export * from "./award";
export * from "./certification";
export * from "./custom-section";
export * from "./education";
export * from "./experience";
export * from "./interest";
export * from "./language";
export * from "./profile";
export * from "./project";
export * from "./publication";
export * from "./reference";
export * from "./skill";
export * from "./volunteer";
